import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Kicker } from './Kicker'
import { Button } from './Button'
import { Field, TextArea, TextInput } from './Field'
import { BottomSheet } from './BottomSheet'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import { absoluteDate } from '@/lib/format'
import type { Person } from '@/lib/types'

interface Pediatrician {
  id: string
  name: string
  clinic: string | null
  phone: string | null
  email: string | null
  address: string | null
  portal_url: string | null
  notes: string | null
}

interface Visit {
  id: string
  visit_at: string
  reason: string | null
  notes: string | null
  followup_at: string | null
}

export function PediatricianSection({ person }: { person: Person }) {
  const familyId = useAppStore((s) => s.familyId)
  const user = useAppStore((s) => s.user)
  const [pedEdit, setPedEdit] = useState<Pediatrician | 'new' | null>(null)
  const [visitEdit, setVisitEdit] = useState<Visit | 'new' | null>(null)

  const pedQ = useQuery({
    enabled: !!familyId,
    queryKey: ['pediatrician', familyId],
    queryFn: async () => {
      const { data, error } = await supabase.from('pediatricians').select('*').eq('family_id', familyId!).maybeSingle()
      if (error) throw error
      return data as Pediatrician | null
    },
  })

  const visitsQ = useQuery({
    enabled: !!familyId && !!person.id,
    queryKey: ['visits', person.id],
    queryFn: async () => {
      const { data, error } = await supabase.from('pediatric_visits').select('id, visit_at, reason, notes, followup_at')
        .eq('family_id', familyId!).eq('person_id', person.id).order('visit_at', { ascending: false }).limit(20)
      if (error) throw error
      return (data ?? []) as Visit[]
    },
  })

  const ped = pedQ.data
  return (
    <section>
      <Kicker>Pediatrician + visits</Kicker>

      {!ped ? (
        <div className="mt-2 glass-tile p-4">
          <div className="text-body text-text-secondary">No pediatrician on file.</div>
          <Button variant="ghost" size="md" className="mt-3" onClick={() => setPedEdit('new')}>+ Add pediatrician</Button>
        </div>
      ) : (
        <div className="mt-2 glass-tile p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-body font-medium text-text-primary">{ped.name}</div>
              {ped.clinic && <div className="text-caption text-text-tertiary">{ped.clinic}</div>}
              {ped.address && <div className="mt-1 text-caption text-text-tertiary">{ped.address}</div>}
              <div className="mt-3 flex flex-wrap gap-2">
                {ped.phone && <a href={`tel:${ped.phone}`} className="pill-btn pill-btn--mint" style={{ minHeight: 36, padding: '8px 14px', fontSize: '12px' }}>Call {ped.phone}</a>}
                {ped.email && <a href={`mailto:${ped.email}`} className="pill-btn pill-btn--ghost" style={{ minHeight: 36, padding: '8px 14px', fontSize: '12px' }}>Email</a>}
                {ped.portal_url && <a href={ped.portal_url} target="_blank" rel="noopener noreferrer" className="pill-btn pill-btn--ghost" style={{ minHeight: 36, padding: '8px 14px', fontSize: '12px' }}>Portal</a>}
              </div>
            </div>
            <button type="button" onClick={() => setPedEdit(ped)} className="text-caption text-text-tertiary underline">Edit</button>
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <div className="text-caption font-medium text-text-secondary">Visits</div>
        <button type="button" onClick={() => setVisitEdit('new')} className="text-caption text-text-secondary underline">+ Log visit</button>
      </div>

      <div className="mt-2 space-y-2">
        {visitsQ.isLoading && <div className="glass-tile p-3 text-caption text-text-tertiary">Loading…</div>}
        {visitsQ.data && visitsQ.data.length === 0 && <div className="glass-tile p-3 text-caption text-text-tertiary">No visits logged yet.</div>}
        {visitsQ.data && visitsQ.data.map((v, i) => (
          <motion.button
            key={v.id}
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, delay: Math.min(i * 0.03, 0.4) }}
            type="button" onClick={() => setVisitEdit(v)}
            className="glass-tile p-3 w-full text-left"
          >
            <div className="text-body text-text-primary">{v.reason ?? 'Visit'}</div>
            <div className="text-caption text-text-tertiary">{absoluteDate(v.visit_at)}{v.followup_at ? ` · follow-up ${absoluteDate(v.followup_at)}` : ''}</div>
            {v.notes && <div className="mt-1 text-caption text-text-secondary line-clamp-2">{v.notes}</div>}
          </motion.button>
        ))}
      </div>

      {pedEdit && <PediatricianSheet familyId={familyId!} initial={pedEdit === 'new' ? null : pedEdit} onClose={() => setPedEdit(null)} onSaved={() => pedQ.refetch()} />}
      {visitEdit && <VisitSheet familyId={familyId!} personId={person.id} userId={user?.id ?? ''} initial={visitEdit === 'new' ? null : visitEdit} onClose={() => setVisitEdit(null)} onSaved={() => visitsQ.refetch()} />}
    </section>
  )
}

function PediatricianSheet({ familyId, initial, onClose, onSaved }: { familyId: string; initial: Pediatrician | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(initial?.name ?? '')
  const [clinic, setClinic] = useState(initial?.clinic ?? '')
  const [phone, setPhone] = useState(initial?.phone ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [address, setAddress] = useState(initial?.address ?? '')
  const [portal, setPortal] = useState(initial?.portal_url ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save() {
    setBusy(true); setErr(null)
    try {
      const row = { family_id: familyId, name: name.trim(), clinic: clinic.trim() || null, phone: phone.trim() || null, email: email.trim() || null, address: address.trim() || null, portal_url: portal.trim() || null, notes: notes.trim() || null }
      if (initial) {
        const { error } = await supabase.from('pediatricians').update(row).eq('id', initial.id)
        if (error) throw new Error(error.message)
      } else {
        const { error } = await supabase.from('pediatricians').insert(row)
        if (error) throw new Error(error.message)
      }
      onSaved(); onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally { setBusy(false) }
  }

  return (
    <BottomSheet
      open={true}
      onClose={onClose}
      title={initial ? 'Edit pediatrician' : 'Add pediatrician'}
      action={<Button variant="primary" size="lg" loading={busy} onClick={save} className="w-full">Save</Button>}
    >
      <div className="space-y-3 pt-3">
        <Field label="Name"><TextInput value={name} onChange={(e) => setName(e.target.value)} autoFocus required /></Field>
        <Field label="Clinic"><TextInput value={clinic} onChange={(e) => setClinic(e.target.value)} /></Field>
        <Field label="Phone" hint="With country code for tap-to-call"><TextInput type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
        <Field label="Email"><TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Address"><TextArea rows={2} value={address} onChange={(e) => setAddress(e.target.value)} /></Field>
        <Field label="Patient portal URL"><TextInput type="url" value={portal} onChange={(e) => setPortal(e.target.value)} /></Field>
        <Field label="Notes"><TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        {err && <div className="rounded-r-sm p-3 text-caption" style={{ background: 'rgba(232,94,110,0.12)', color: '#D14758' }}>{err}</div>}
      </div>
    </BottomSheet>
  )
}

function VisitSheet({ familyId, personId, userId, initial, onClose, onSaved }: { familyId: string; personId: string; userId: string; initial: Visit | null; onClose: () => void; onSaved: () => void }) {
  const [visitAt, setVisitAt] = useState(initial?.visit_at.slice(0, 16) ?? new Date().toISOString().slice(0, 16))
  const [reason, setReason] = useState(initial?.reason ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [followupAt, setFollowupAt] = useState(initial?.followup_at?.slice(0, 16) ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save() {
    setBusy(true); setErr(null)
    try {
      const row = {
        family_id: familyId,
        person_id: personId,
        visit_at: new Date(visitAt).toISOString(),
        reason: reason.trim() || null,
        notes: notes.trim() || null,
        followup_at: followupAt ? new Date(followupAt).toISOString() : null,
        created_by: userId,
      }
      if (initial) {
        const { error } = await supabase.from('pediatric_visits').update(row).eq('id', initial.id)
        if (error) throw new Error(error.message)
      } else {
        const { error } = await supabase.from('pediatric_visits').insert(row)
        if (error) throw new Error(error.message)
      }
      onSaved(); onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally { setBusy(false) }
  }

  return (
    <BottomSheet
      open={true}
      onClose={onClose}
      title={initial ? 'Edit visit' : 'Log visit'}
      action={<Button variant="primary" size="lg" loading={busy} onClick={save} className="w-full">Save</Button>}
    >
      <div className="space-y-3 pt-3">
        <Field label="When"><TextInput type="datetime-local" value={visitAt} onChange={(e) => setVisitAt(e.target.value)} /></Field>
        <Field label="Reason"><TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Well visit, fever, ear infection…" /></Field>
        <Field label="Notes"><TextArea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Doctor's observations, medications prescribed, follow-up needed." /></Field>
        <Field label="Follow-up (optional)"><TextInput type="datetime-local" value={followupAt} onChange={(e) => setFollowupAt(e.target.value)} /></Field>
        {err && <div className="rounded-r-sm p-3 text-caption" style={{ background: 'rgba(232,94,110,0.12)', color: '#D14758' }}>{err}</div>}
      </div>
    </BottomSheet>
  )
}
